import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Package, CheckCircle2, Truck, Home, Loader2, Phone, MessageSquare, Star, MapPin, Clock } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
const YARD_LOCATION: [number, number] = [47.3175, -122.3128];
const DEST_LOCATION: [number, number] = [47.2529, -122.4443];
// Simple straight line interpolation for demo
function interpolatePosition(start: [number, number], end: [number, number], progress: number): [number, number] {
  return [start[0] + (end[0] - start[0]) * progress, start[1] + (end[1] - start[1]) * progress];
}
// Custom Icons
const truckIcon = L.divIcon({
  className: 'custom-truck-icon',
  html: '<div style="background-color: #C0B283; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.3); border: 2px solid white; font-size: 16px;">🚚</div>',
  iconSize: [32, 32],
  iconAnchor: [16, 16]
});
const pulsatingIcon = L.divIcon({
  className: 'custom-div-icon',
  html: '<div class="pulsating-pin" style="background-color: #1DB954;"><div class="pulsating-pin-dot" style="background-color: #1DB954;"></div></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});
const yardIcon = L.divIcon({
  className: 'custom-div-icon',
  html: '<div style="background-color: #474747; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white;"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12]
});
// Component to auto-fit bounds
function MapBounds({
  start,
  end,
  current
}: {
  start: [number, number];
  end: [number, number];
  current: [number, number];
}) {
  const map = useMap();
  useEffect(() => {
    const bounds = L.latLngBounds([start, end]);
    map.fitBounds(bounds, {
      padding: [50, 50]
    });
  }, [map, start, end]);
  // Optionally pan to keep truck in view if zoomed in
  useEffect(() => {
    // map.panTo(current, { animate: true, duration: 0.5 })
  }, [current, map]);
  return null;
}
export function OrderStatus() {
  const [view, setView] = useState<'lookup' | 'tracking' | 'delivered'>('lookup');
  const [lookupValue, setLookupValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  // Tracking state
  const [progress, setProgress] = useState(0); // 0 to 1
  const [truckPos, setTruckPos] = useState<[number, number]>(YARD_LOCATION);
  const [etaMinutes, setEtaMinutes] = useState(12);
  const [rating, setRating] = useState(0);
  const TOTAL_DURATION_MS = 30000; // 30 seconds for demo
  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!lookupValue.trim() && !e) return; // Allow demo bypass
    setIsSearching(true);
    setTimeout(() => {
      setIsSearching(false);
      setView('tracking');
      setProgress(0);
      setTruckPos(YARD_LOCATION);
      setEtaMinutes(12);
    }, 800);
  };
  // Animation loop for tracking
  useEffect(() => {
    if (view !== 'tracking') return;
    let startTime = Date.now();
    let animationFrameId: number;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      let newProgress = elapsed / TOTAL_DURATION_MS;
      if (newProgress >= 1) {
        newProgress = 1;
        setProgress(1);
        setTruckPos(DEST_LOCATION);
        setEtaMinutes(0);
        setTimeout(() => setView('delivered'), 1000);
        return;
      }
      setProgress(newProgress);
      setTruckPos(interpolatePosition(YARD_LOCATION, DEST_LOCATION, newProgress));
      setEtaMinutes(Math.ceil(12 * (1 - newProgress)));
      animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [view]);
  const getStage = () => {
    if (progress < 0.1) return 0; // Loaded
    if (progress < 0.7) return 1; // En Route
    if (progress < 0.99) return 2; // Arriving
    return 3; // Delivered
  };
  const currentStage = getStage();
  const stageLabels = [{
    label: 'Loaded',
    icon: Package
  }, {
    label: 'En Route',
    icon: Truck
  }, {
    label: 'Arriving',
    icon: MapPin
  }, {
    label: 'Delivered',
    icon: CheckCircle2
  }];
  const getStatusText = () => {
    if (progress < 0.1) return 'Loaded at yard';
    if (progress < 0.7) return 'En route to destination';
    if (progress < 0.99) return 'Almost there';
    return 'Arriving now';
  };
  return <main className="relative w-full min-h-[calc(100vh-113px)] bg-[#fdfbf7] dark:bg-[#222222] overflow-hidden flex flex-col" data-id="element-1871">
      <AnimatePresence mode="wait" data-id="element-1872">
        {view === 'lookup' && <motion.div key="lookup" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 z-10" data-id="element-1873">
            <div className="max-w-xl w-full" data-id="element-1874">
              <div className="text-center mb-10" data-id="element-1875">
                <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-stone-900 dark:text-white mb-4" data-id="element-1876">
                  Track Your <span className="text-brand-orange" data-id="element-1877">Order</span>
                </h1>
                <p className="text-lg text-stone-600 dark:text-stone-300" data-id="element-1878">
                  Enter your phone number or email to check your live delivery
                  status.
                </p>
              </div>

              <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl shadow-xl border border-stone-200 dark:border-white/10 p-6 sm:p-8 mb-6" data-id="element-1879">
                <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4" data-id="element-1880">
                  <div className="flex-1 relative" data-id="element-1881">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1882" />
                    <input type="text" value={lookupValue} onChange={e => setLookupValue(e.target.value)} placeholder="Phone number or email..." className="w-full pl-12 pr-4 py-4 bg-stone-50 dark:bg-black/20 border border-stone-200 dark:border-white/10 rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required data-id="element-1883" />
                  </div>
                  <button type="submit" disabled={isSearching || !lookupValue.trim()} className="px-8 py-4 bg-brand-button hover:bg-brand-buttonHover disabled:opacity-50 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 shrink-0" data-id="element-1884">
                    {isSearching ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-1885" /> : 'Track Order'}
                  </button>
                </form>
              </div>

              <div className="text-center" data-id="element-1886">
                <button onClick={() => {
              setLookupValue('ORDER-2531');
              handleSearch();
            }} className="text-sm text-brand-orange font-medium hover:underline flex items-center justify-center mx-auto gap-1" data-id="element-1887">
                  <Truck className="w-4 h-4" data-id="element-1888" />
                  Demo: Try ORDER-2531
                </button>
              </div>
            </div>
          </motion.div>}

        {(view === 'tracking' || view === 'delivered') && <motion.div key="tracking" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="absolute inset-0 flex flex-col" data-id="element-1889">
            {/* Map Background */}
            <div className="absolute inset-0 z-0 bg-stone-800" data-id="element-1890">
              <MapContainer center={YARD_LOCATION} zoom={11} zoomControl={false} scrollWheelZoom={false} dragging={false} className="w-full h-full" data-id="element-1891">
                <TileLayer url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" data-id="element-1892" />
                <MapBounds start={YARD_LOCATION} end={DEST_LOCATION} current={truckPos} data-id="element-1893" />

                <Polyline positions={[YARD_LOCATION, DEST_LOCATION]} pathOptions={{
              color: '#C0B283',
              weight: 4,
              dashArray: '8, 8',
              opacity: 0.8
            }} data-id="element-1894" />

                <Marker position={YARD_LOCATION} icon={yardIcon} data-id="element-1895" />
                <Marker position={DEST_LOCATION} icon={pulsatingIcon} data-id="element-1896" />

                {view === 'tracking' && <Marker position={truckPos} icon={truckIcon} zIndexOffset={1000} data-id="element-1897" />}
              </MapContainer>
              {/* Dark Overlay for better contrast */}
              <div className="absolute inset-0 bg-black/20 pointer-events-none z-[400]" data-id="element-1898" />
            </div>

            {/* UI Overlays */}
            <div className="relative z-[500] flex-1 flex flex-col pointer-events-none p-4 sm:p-6 lg:p-8" data-id="element-1899">
              {/* Top Section: Status Panel */}
              <div className="flex justify-end" data-id="element-1900">
                <AnimatePresence mode="wait" data-id="element-1901">
                  {view === 'tracking' ? <motion.div key="status-panel" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} className="bg-[#474747]/95 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl w-full max-w-sm pointer-events-auto" data-id="element-1902">
                      <div className="flex justify-between items-start mb-4" data-id="element-1903">
                        <div data-id="element-1904">
                          <p className="text-brand-orange font-bold text-sm tracking-wider uppercase" data-id="element-1905">
                            ORDER-2531
                          </p>
                          <h2 className="text-white font-heading font-bold text-xl mt-1" data-id="element-1906">
                            Sarah Jenkins
                          </h2>
                        </div>
                        <div className="text-right" data-id="element-1907">
                          <p className="text-stone-400 text-xs font-bold uppercase tracking-wider" data-id="element-1908">
                            ETA
                          </p>
                          <p className="text-white font-heading font-bold text-3xl leading-none mt-1" data-id="element-1909">
                            {etaMinutes}
                            <span className="text-lg text-stone-400 ml-1" data-id="element-1910">
                              min
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="bg-black/20 rounded-xl p-4 mb-4" data-id="element-1911">
                        <p className="text-stone-300 text-sm font-medium flex items-center gap-2" data-id="element-1912">
                          <Package className="w-4 h-4 text-brand-orange" data-id="element-1913" />
                          12 yds 5/8" Minus + Delivery
                        </p>
                      </div>

                      <div className="flex items-center gap-3 mb-4" data-id="element-1914">
                        <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/10 shrink-0" data-id="element-1915">
                          <img src="https://i.pravatar.cc/100?img=12" alt="Driver Mike" className="w-full h-full object-cover" data-id="element-1916" />
                        </div>
                        <div data-id="element-1917">
                          <p className="text-white font-bold text-sm" data-id="element-1918">Mike</p>
                          <p className="text-stone-400 text-xs font-medium" data-id="element-1919">
                            Truck 03
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2" data-id="element-1920">
                        <a href="tel:2065550123" className="flex-1 bg-white/10 hover:bg-white/20 text-white py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-bold transition-colors" data-id="element-1921">
                          <Phone className="w-4 h-4" data-id="element-1922" /> Call
                        </a>
                        <a href="sms:2065550123" className="flex-1 bg-white/10 hover:bg-white/20 text-white py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-bold transition-colors" data-id="element-1923">
                          <MessageSquare className="w-4 h-4" data-id="element-1924" /> Text
                        </a>
                      </div>
                    </motion.div> : <motion.div key="success-panel" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="bg-[#474747]/95 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl w-full max-w-sm pointer-events-auto flex flex-col items-center text-center" data-id="element-1925">
                      <div className="w-16 h-16 bg-[#1DB954]/20 rounded-full flex items-center justify-center mb-4" data-id="element-1926">
                        <CheckCircle2 className="w-8 h-8 text-[#1DB954]" data-id="element-1927" />
                      </div>
                      <h2 className="text-white font-heading font-bold text-2xl mb-1" data-id="element-1928">
                        Delivered!
                      </h2>
                      <p className="text-stone-300 text-sm mb-6" data-id="element-1929">
                        Your materials have arrived at 123 Main St.
                      </p>

                      <div className="w-full h-32 rounded-xl overflow-hidden mb-6 border border-white/10" data-id="element-1930">
                        <img src="https://cdn.magicpatterns.com/uploads/gju6hVSPcTW63PScwXbTpM/file_00000000afac71fd869ff743c7d17b4b~2.png" alt="Delivery photo" className="w-full h-full object-cover" data-id="element-1931" />
                      </div>

                      <p className="text-white font-bold text-sm mb-3" data-id="element-1932">
                        Rate your delivery
                      </p>
                      <div className="flex gap-2 mb-6" data-id="element-1933">
                        {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setRating(star)} className="focus:outline-none" data-id="element-1934">
                            <Star className={`w-8 h-8 ${rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-stone-500'}`} data-id="element-1935" />
                          </button>)}
                      </div>

                      <button onClick={() => {
                  setView('lookup');
                  setLookupValue('');
                }} className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white py-3 rounded-xl font-bold transition-colors" data-id="element-1936">
                        Done
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>

              <div className="flex-1" data-id="element-1937" />

              {/* Bottom Section: Progress Strip */}
              {view === 'tracking' && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-[#474747]/95 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-white/10 shadow-2xl pointer-events-auto mt-4" data-id="element-1938">
                  <div className="flex items-center justify-between mb-4" data-id="element-1939">
                    <h3 className="text-white font-bold text-lg flex items-center gap-2" data-id="element-1940">
                      <Clock className="w-5 h-5 text-brand-orange" data-id="element-1941" />
                      {getStatusText()}
                    </h3>
                    <span className="text-stone-400 text-sm font-medium" data-id="element-1942">
                      {Math.round(progress * 100)}%
                    </span>
                  </div>
                  <div className="relative" data-id="element-1943">
                    {/* Background Line */}
                    <div className="absolute top-1/2 left-0 right-0 h-1 bg-white/10 -translate-y-1/2 rounded-full" data-id="element-1944" />

                    {/* Active Line */}
                    <motion.div className="absolute top-1/2 left-0 h-1 bg-brand-orange -translate-y-1/2 rounded-full" initial={{
                width: '0%'
              }} animate={{
                width: `${progress * 100}%`
              }} transition={{
                ease: 'linear',
                duration: 0.1
              }} data-id="element-1945" />

                    {/* Stage Nodes */}
                    <div className="relative flex justify-between" data-id="element-1946">
                      {stageLabels.map((stage, idx) => {
                  const isCompleted = currentStage >= idx;
                  const isActive = currentStage === idx;
                  const Icon = stage.icon;
                  return <div key={stage.label} className="flex flex-col items-center" data-id="element-1947">
                            <motion.div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 z-10 transition-colors duration-300 ${isCompleted ? 'bg-brand-orange border-brand-orange text-white' : 'bg-[#333] border-white/20 text-stone-500'}`} animate={isActive ? {
                      scale: [1, 1.1, 1]
                    } : {}} transition={{
                      repeat: isActive ? Infinity : 0,
                      duration: 2
                    }} data-id="element-1948">
                              {isCompleted && idx < currentStage ? <Check className="w-4 h-4" data-id="element-1949" /> : <Icon className="w-4 h-4" data-id="element-1950" />}
                            </motion.div>
                            <span className={`text-xs font-bold mt-2 absolute top-8 whitespace-nowrap transition-colors duration-300 ${isCompleted ? 'text-white' : 'text-stone-500'}`} data-id="element-1951">
                              {stage.label}
                            </span>
                          </div>;
                })}
                    </div>
                  </div>
                  <div className="h-6" data-id="element-1952" /> {/* Spacer for absolute text */}
                </motion.div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </main>;
}